import { useCallback, useEffect, useRef } from 'react';
import { publishIdleClock } from '../../utils/app/idleClockSnapshot';
import { getIdleTimeoutMs, getIdleWarningLeadMs, idleTestActive } from '../../config/sessionPolicy';

const ACTIVITY_EVENTS = ['click', 'touchstart', 'keydown', 'scroll'];
const THROTTLE_MS = 30_000; // Update lastActivity at most once per 30s
const CHECK_INTERVAL_MS = 60_000; // Check idle status every 60s
const WARNING_CHECK_INTERVAL_MS = 1_000; // …and every 1s while the warning is up, so the countdown and the real sign-out are the same clock

/**
 * Role-based idle session timeout.
 *
 * Tracks user activity (clicks, touches, keypresses, scrolls) and triggers
 * a warning callback before auto-logout when the user has been idle too long.
 *
 * @param {object} config
 * @param {boolean} config.enabled     – false while loading, logged out, or offline
 * @param {number|Function} config.timeoutMs – idle threshold in ms (role-dependent).
 *                                              Pass a function to re-evaluate each
 *                                              check tick — needed when the timeout
 *                                              depends on context that can change
 *                                              without re-rendering the caller
 *                                              (e.g. URL pathname for the platform-
 *                                              vs-tenant context distinction).
 * @param {number}  [config.warningMs] – how early to warn before timeout (default 2 min)
 * @param {Function} config.onWarning  – called once when warning threshold is reached
 * @param {Function} config.onTimeout  – called when idle timeout expires (should sign out)
 * @returns {{getRemainingMs: () => (number|null), getLastActivityAt: () => number}} ms until the real sign-out
 *   (same lastActivity + timeout the check tick uses); null when unknown.
 */
export function useIdleTimeout({
  enabled,
  timeoutMs,
  warningMs,
  onWarning,
  onTimeout,
}) {
  // Latest timeoutMs via ref: the check tick and getRemainingMs share ONE resolver.
  const timeoutMsRef = useRef(timeoutMs);
  timeoutMsRef.current = timeoutMs;
  const resolveTimeoutMs = useCallback(() => {
    const t = timeoutMsRef.current;
    return typeof t === 'function' ? t() : t;
  }, []);
  const lastActivityRef = useRef(Date.now());
  const warningFiredRef = useRef(false);
  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);

  // Keep callback refs current without re-registering effects.
  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  const getRemainingMs = useCallback(() => {
    const t = resolveTimeoutMs();
    if (!t) return null;
    return Math.max(0, t - (Date.now() - lastActivityRef.current));
  }, [resolveTimeoutMs]);

  // Publish the clock for the silent app-update gate (KAN-886): it refuses to
  // reload near an inactivity sign-out. Read-only — changes no idle behaviour.
  useEffect(() => publishIdleClock({
    enabled: Boolean(enabled),
    getRemainingMs,
    get leadMs() { return warningMs ?? getIdleWarningLeadMs(); },
  }), [enabled, warningMs, getRemainingMs]);

  // Reset the idle clock ONLY when the hook (re-)enables or the RESOLVED timeout
  // NUMBER changes (role change). Never on `timeoutMs` function identity or a
  // re-render: an inline arrow from the caller is a new function every render,
  // and the warning's own setState is a render — that reset the clock forever
  // (KAN-608 security review; on main it made sign-out fire at ~2x the timeout).
  const resolvedTimeoutMs = resolveTimeoutMs();
  useEffect(() => {
    if (enabled) {
      lastActivityRef.current = Date.now();
      warningFiredRef.current = false;
    }
  }, [enabled, resolvedTimeoutMs]);

  // Global activity listeners (throttled).
  useEffect(() => {
    if (!enabled) return;

    let lastUpdate = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate < THROTTLE_MS) return;
      lastUpdate = now;
      lastActivityRef.current = now;
      warningFiredRef.current = false;
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true, capture: true });
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity, { capture: true });
      }
    };
  }, [enabled]);

  // Periodic idle check. Resolves the timeout each tick so callers can pass
  // a function form that returns a context-dependent value (e.g. shorter on
  // /platform routes, longer in tenant work).
  useEffect(() => {
    if (!enabled) return undefined;

    let fastId = null;
    let lastTimeoutCallAt = 0;
    const stopFast = () => { if (fastId != null) { clearInterval(fastId); fastId = null; } };
    const check = () => {
      if (!warningFiredRef.current) stopFast(); // activity reset the warning
      if (!navigator.onLine) return; // Skip when offline — offline grace handles that
      const currentTimeoutMs = resolveTimeoutMs();
      if (!currentTimeoutMs) return;
      const lead = warningMs ?? getIdleWarningLeadMs();
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= currentTimeoutMs) {
        // Keep the 1s tick: a failed sign-out is retried on the next second, and
        // the effect cleanup stops it once the session is really gone.
        // The 60s and 1s ticks can land in the same instant — one call per ~second.
        if (Date.now() - lastTimeoutCallAt >= 500) {
          lastTimeoutCallAt = Date.now();
          onTimeoutRef.current?.();
        }
        return;
      }

      if (idle >= currentTimeoutMs - lead && !warningFiredRef.current) {
        warningFiredRef.current = true;
        if (fastId == null) fastId = setInterval(check, WARNING_CHECK_INTERVAL_MS);
        onWarningRef.current?.();
      }
    };

    // Normal timeouts keep the 60s cadence (2 min lead). The dev idle-test switch
    // (60s timeout / 30s lead) needs 1s ticks or the first check lands past the
    // timeout and the warning never fires.
    const id = setInterval(check, idleTestActive() ? WARNING_CHECK_INTERVAL_MS : CHECK_INTERVAL_MS);
    return () => { clearInterval(id); stopFast(); };
  }, [enabled, warningMs, resolveTimeoutMs]);
  // When the last REAL user activity (DOM event / re-enable) happened.
  const getLastActivityAt = useCallback(() => lastActivityRef.current, []);

  return { getRemainingMs, getLastActivityAt };
}

// Re-exported from the single source of truth so existing importers keep working.
export { getIdleTimeoutMs };
